import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@devhop/ui";
import { useEffect, useState } from "react";
import type { EventOutsourceDTO } from "../../domain/contracts";
import {
  useOutsourceApInstallmentPlanTemplatesLookup,
  useSaveEventOutsourceInstallmentPlan,
} from "../api/queries";
import { formatOutsourceMoney } from "./outsource-table-cells";

type EventOutsourceInstallmentPlanDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  row: EventOutsourceDTO | null;
};

export function EventOutsourceInstallmentPlanDialog({
  open,
  onOpenChange,
  eventId,
  row,
}: EventOutsourceInstallmentPlanDialogProps) {
  const savePlan = useSaveEventOutsourceInstallmentPlan(eventId);
  const templatesQuery = useOutsourceApInstallmentPlanTemplatesLookup();
  const [mode, setMode] = useState<"lump_sum" | "installments">("lump_sum");
  const [templateId, setTemplateId] = useState("");

  useEffect(() => {
    if (!open || !row) return;
    setMode(row.paymentMode ?? "lump_sum");
    setTemplateId("");
  }, [open, row]);

  if (!row) return null;

  const templates = templatesQuery.data?.items ?? [];
  const locked = (row.installments ?? []).some(
    (i) => i.status === "paid" || i.status === "in_payout",
  );

  const onSubmit = async () => {
    if (mode === "lump_sum") {
      await savePlan.mutateAsync({
        rowId: row.id,
        input: { paymentMode: "lump_sum" },
      });
      onOpenChange(false);
      return;
    }
    if (!templateId) return;
    await savePlan.mutateAsync({
      rowId: row.id,
      input: {
        paymentMode: "installments",
        templateId,
        planAnchorDate: new Date().toISOString().slice(0, 10),
      },
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>ແຜນຈ່າຍ Outsource (AP)</DialogTitle>
          <DialogDescription>
            {row.displayName} · ຍອດຕົກລົງ {formatOutsourceMoney(row.agreedAmount)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>ຮູບແບບຈ່າຍ</Label>
            <Select
              value={mode}
              onValueChange={(v) =>
                setMode(v as "lump_sum" | "installments")
              }
              disabled={locked}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lump_sum">ຈ່າຍຄັ້ງດຽວ (1 ໃບເບີກ)</SelectItem>
                <SelectItem value="installments">ແບ່ງງວດ (1 ໃບ/ງວດ)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {mode === "installments" ? (
            <div className="space-y-2">
              <Label>ແມ່ແບບງວດ AP</Label>
              <Select
                value={templateId}
                onValueChange={setTemplateId}
                disabled={locked || templatesQuery.isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="ເລືອກແມ່ແບບ..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(row.installments?.length ?? 0) > 0 ? (
                <ul className="rounded-md border bg-muted/30 p-2 text-sm">
                  {row.installments!.map((inst) => (
                    <li key={inst.id} className="flex justify-between gap-2">
                      <span>
                        ງວດ {inst.installmentNo}
                        {inst.label ? ` · ${inst.label}` : ""}
                      </span>
                      <span className="tabular-nums">
                        {formatOutsourceMoney(inst.amount)} ({inst.status})
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          {locked ? (
            <p className="text-amber-800 text-sm dark:text-amber-200">
              ມີງວດຈ່າຍ/ໃບເບີກແລ້ວ — ປ່ຽນແຜນບໍ່ໄດ້
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            ຍົກເລີກ
          </Button>
          <Button
            type="button"
            disabled={
              savePlan.isPending ||
              (mode === "installments" && !templateId && !locked)
            }
            onClick={() => void onSubmit()}
          >
            {savePlan.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
